import {
  AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy,
  Output, Renderer2
} from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { DomHandler } from '../utils/dom-handler';
import { BaseComponent } from '../general/base/base.component';

@Component({
  selector: 'qm-overlay-panel',
  templateUrl: './overlay-panel.component.html',
  styleUrls: ['./overlay-panel.component.scss'],
  animations: [
    trigger('panelState', [
      state('hidden', style({
        opacity: 0
      })),
      state('visible', style({
        opacity: 1
      })),
      transition('visible => hidden', animate('400ms ease-in')),
      transition('hidden => visible', animate('400ms ease-out'))
    ])
  ],
  providers: [DomHandler]
})
export class OverlayPanelComponent extends BaseComponent implements AfterViewInit, AfterViewChecked, OnDestroy {

  @Input() zIndex: number;

  @Input() dismissable: boolean = true;

  @Input() showCloseIcon: boolean;

  @Input() appendTo: string | HTMLElement;

  @Input() stickTo: 'right-bottom' | 'right-top' | 'left-bottom';

  @Input() skipFirstClick: boolean = true;

  @Output() onBeforeShow: EventEmitter<any> = new EventEmitter();

  @Output() onAfterShow: EventEmitter<any> = new EventEmitter();

  @Output() onBeforeHide: EventEmitter<any> = new EventEmitter();

  @Output() onAfterHide: EventEmitter<any> = new EventEmitter();

  container: HTMLElement;

  visible: boolean = false;

  documentClickListener: () => void;

  selfClick: boolean;

  target: HTMLElement;

  willHide: boolean;

  willShow: boolean;

  targetClickEvent: boolean;

  closeClick: boolean;

  constructor(public el: ElementRef, public domHandler: DomHandler,
              public renderer: Renderer2, private cd: ChangeDetectorRef) {
    super();
    this.name = 'OverlayPanelComponent';
  }

  ngAfterViewInit() {
    this.container = this.el.nativeElement.children[0];
    if (this.appendTo) {
      if (this.appendTo === 'body') {
        document.body.appendChild(this.container);
      } else {
        this.domHandler.appendChild(this.container, this.appendTo);
      }
    }
  }

  ngAfterViewChecked() {
    if (this.willShow) {
      this.domHandler.absolutePosition(this.container, this.target, this.stickTo);
      this.bindDocumentClickListener();
      this.onAfterShow.emit(null);
      this.willShow = false;
    }
    if (this.willHide) {
      this.onAfterHide.emit(null);
      this.willHide = false;
    }
  }

  public bindDocumentClickListener() {
    if (!this.documentClickListener && this.dismissable) {
      this.documentClickListener = this.renderer.listen('document', 'click', () => {
        if (!this.selfClick && !this.targetClickEvent && this.dismissable) {
          this.hide();
        }
        this.selfClick = false;
        this.targetClickEvent = false;
        this.cd.markForCheck();
      });
    }
  }

  unbindDocumentClickListener() {
    if (this.documentClickListener) {
      this.documentClickListener();
      this.documentClickListener = null;
    }
  }

  toggle(event: Event, target?: HTMLElement) {
    if (!this.target || this.target === (target || event.currentTarget || event.target)) {
      if (this.visible) {
        this.hide();
      } else {
        this.show(event, target);
      }
    } else {
      this.show(event, target);
    }
  }

  show(event: Event, target?: HTMLElement) {
    this.onBeforeShow.emit(null);
    this.target = (target || event.currentTarget || event.target) as HTMLElement;
    this.container.style.zIndex = ++DomHandler.zindex + '';

    this.visible = true;
    this.willShow = true;

    if (this.skipFirstClick) {
      this.targetClickEvent = true;
    }
  }

  hide() {
    if (this.visible) {
      this.onBeforeHide.emit(null);
      this.willHide = true;
      this.visible = false;
      this.selfClick = false;
      this.targetClickEvent = false;
      this.unbindDocumentClickListener();
    }
  }

  onPanelClick(event) {
    if (this.closeClick) {
      this.hide();
      this.closeClick = false;
    } else if (this.dismissable) {
      this.selfClick = true;
    }
  }

  onCloseClick(event) {
    this.closeClick = true;
    event.preventDefault();
  }

  ngOnDestroy() {
    this.unbindDocumentClickListener();

    if (this.appendTo) {
      this.el.nativeElement.appendChild(this.container);
    }

    this.target = null;
  }
}

